'use client';

import { c as _c } from "react/compiler-runtime";
import { CountryCode } from 'libphonenumber-js';
import { useEffect, useRef } from 'react';
import { ButtonGroup, InputGroup } from '@repo/ui/components/start';
import { cn } from '@repo/ui/lib/utils';
import { usePhoneInput } from './context';
import { PhoneInputAction } from './action';
import { PhoneInputCopyMenu } from './copy-menu';
import { PhoneInputCountrySelect } from './country-select';
import { PhoneInputField } from './field';
import { PhoneInputModule } from './module';
import { PhoneInputProvider } from './context';
import type { PhoneInputInputVariant, PhoneInputLayout } from './types';
export interface PhoneInputProps extends Omit<React.ComponentProps<'input'>, 'type'> {
  children?: React.ReactNode;
  country?: CountryCode | null;
  defaultCountry?: CountryCode;
  inputVariant?: PhoneInputInputVariant;
  onCountryChange?: (country: CountryCode) => void;
  onGroupBlur?: () => void;
  variant?: PhoneInputLayout;
}
const normalizeValue = (v: unknown) => v === undefined || v === null ? undefined : String(v);
function PhoneInputBlurWrapper(t0) {
  const $ = _c(8);
  const {
    children,
    onGroupBlur
  } = t0;
  const {
    state
  } = usePhoneInput();
  const ref = useRef(null);
  const timeoutRef = useRef(undefined);
  let t1;
  if ($[0] !== onGroupBlur || $[1] !== state) {
    t1 = () => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        if (state.countryOpen) {
          return;
        }
        if (ref.current && !ref.current.contains(document.activeElement)) {
          onGroupBlur?.();
        }
      }, 0);
    };
    $[0] = onGroupBlur;
    $[1] = state;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleBlur = t1;
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => clearTimeout(timeoutRef.current);
    t3 = [];
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== children || $[6] !== handleBlur) {
    t4 = <div className="w-full" onBlur={handleBlur} ref={ref} data-sentry-component="PhoneInputBlurWrapper" data-sentry-source-file="phone-input.tsx">{children}</div>;
    $[5] = children;
    $[6] = handleBlur;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function PhoneInputRoot(t0) {
  const $ = _c(43);
  let children;
  let className;
  let country;
  let defaultCountry;
  let defaultValue;
  let disabled;
  let inputProps;
  let onChange;
  let onCountryChange;
  let onGroupBlur;
  let ref;
  let t1;
  let t2;
  let t3;
  let value;
  if ($[0] !== t0) {
    ({
      children,
      className,
      country,
      defaultCountry,
      defaultValue,
      disabled,
      inputVariant: t1,
      onChange,
      onCountryChange,
      onGroupBlur,
      placeholder: t2,
      ref,
      value,
      variant: t3,
      ...inputProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = country;
    $[4] = defaultCountry;
    $[5] = defaultValue;
    $[6] = disabled;
    $[7] = inputProps;
    $[8] = onChange;
    $[9] = onCountryChange;
    $[10] = onGroupBlur;
    $[11] = ref;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = value;
  } else {
    children = $[1];
    className = $[2];
    country = $[3];
    defaultCountry = $[4];
    defaultValue = $[5];
    disabled = $[6];
    inputProps = $[7];
    onChange = $[8];
    onCountryChange = $[9];
    onGroupBlur = $[10];
    ref = $[11];
    t1 = $[12];
    t2 = $[13];
    t3 = $[14];
    value = $[15];
  }
  const inputVariant = t1 === undefined ? "default" : t1;
  const placeholder = t2 === undefined ? "-" : t2;
  const variant = t3 === undefined ? "input-group" : t3;
  let t4;
  if ($[16] !== defaultValue) {
    t4 = normalizeValue(defaultValue);
    $[16] = defaultValue;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== value) {
    t5 = normalizeValue(value);
    $[18] = value;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== children || $[21] !== className || $[22] !== disabled || $[23] !== inputProps || $[24] !== inputVariant || $[25] !== placeholder || $[26] !== ref || $[27] !== variant) {
    t6 = variant === "button-group" ? <ButtonGroup className={cn("w-full", "group/phone-input", className)}>{children ?? <><PhoneInputCountrySelect /><PhoneInputField placeholder={placeholder} ref={ref} {...inputProps} /><PhoneInputAction /></>}</ButtonGroup> : <InputGroup className={cn("group/phone-input w-full", inputVariant === "ghost" && "has-disabled:bg-transparent has-[[data-slot=input-group-control]:focus-visible]:border-transparent has-[[data-slot=input-group-control]:focus-visible]:ring-0", className)} data-disabled={disabled ? "true" : undefined} variant={inputVariant}>{children ?? <><PhoneInputCountrySelect /><PhoneInputField placeholder={placeholder} ref={ref} {...inputProps} className="group-focus/phone-input:pl-0!" /><PhoneInputAction /></>}</InputGroup>;
    $[20] = children;
    $[21] = className;
    $[22] = disabled;
    $[23] = inputProps;
    $[24] = inputVariant;
    $[25] = placeholder;
    $[26] = ref;
    $[27] = variant;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== onGroupBlur || $[30] !== t6) {
    t7 = <PhoneInputBlurWrapper onGroupBlur={onGroupBlur} data-sentry-element="PhoneInputBlurWrapper" data-sentry-source-file="phone-input.tsx">{t6}</PhoneInputBlurWrapper>;
    $[29] = onGroupBlur;
    $[30] = t6;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] !== country || $[33] !== defaultCountry || $[34] !== disabled || $[35] !== inputVariant || $[36] !== onChange || $[37] !== onCountryChange || $[38] !== t4 || $[39] !== t5 || $[40] !== t7 || $[41] !== variant) {
    t8 = <PhoneInputProvider country={country} defaultCountry={defaultCountry} defaultValue={t4} disabled={disabled} inputVariant={inputVariant} layout={variant} onChange={onChange} onCountryChange={onCountryChange} value={t5} data-sentry-element="PhoneInputProvider" data-sentry-component="PhoneInputRoot" data-sentry-source-file="phone-input.tsx">{t7}</PhoneInputProvider>;
    $[32] = country;
    $[33] = defaultCountry;
    $[34] = disabled;
    $[35] = inputVariant;
    $[36] = onChange;
    $[37] = onCountryChange;
    $[38] = t4;
    $[39] = t5;
    $[40] = t7;
    $[41] = variant;
    $[42] = t8;
  } else {
    t8 = $[42];
  }
  return t8;
}
export const PhoneInput = Object.assign(PhoneInputRoot, {
  Action: PhoneInputAction,
  CopyMenu: PhoneInputCopyMenu,
  CountrySelect: PhoneInputCountrySelect,
  Field: PhoneInputField,
  Module: PhoneInputModule
});